import { methodFieldId } from '@/components/admin/reglages/method-contracts';
import { type MethodFieldRef, isRequired, maxOf, problemOf } from '@/lib/method-draft';
import {
  METHOD_FUNCTION_FIELD_LABEL,
  METHOD_OPTIONAL_MARK,
  METHOD_STYLE_FIELD_LABEL,
  METHOD_TOUCHED_MARK,
  METHOD_TYPICAL_WORDS_PLACEHOLDER,
  characterCount,
  fieldRefusal,
} from '@/lib/method-labels';
import { cn } from '@/lib/utils';
import { useId } from 'react';

/**
 * One field of a card of Réglages > Méthode (analyste.md §4.2, maquette
 * `Methode`): its label, the tag that says it moved, the control, the counter
 * where the planche draws one, and the refusal under it.
 *
 * ## The bound is a CAP, and the refusal stands behind it
 *
 * `maxLength` cuts the typing and the pasting at the bound - the régime of the
 * direction of a criterion and of a persona, the other editors whose draft is
 * autosaved - and `withFunctionField` / `withStyleField` cut whatever comes in
 * otherwise. It is not a courtesy: the draft schema keeps the bounds, so a draft
 * that held a value past one would be refused by EVERY autosave, and the
 * retouches of the other fields would never reach the server (`method-draft.ts`).
 * The refusal under the field (« 41 caractères : 40 au plus ») is the mirror of
 * the schema for a value that reaches the field some other way, measured the way
 * the schema measures - the blanks of the edges are not counted - so the counter,
 * the refusal and the server agree; typing cannot reach it.
 *
 * ## The colour says what moved
 *
 * A field that differs from the development version wears the yellow of the
 * planche (`jaune-deep` border on `jaune-tint`) and its tag « modifié » - the same
 * signal as the head's « Brouillon modifié ». Every state keeps the 2 px border, so
 * typing the first letter does not move the page by a pixel.
 *
 * ## Reading is `readonly`, never `disabled`
 *
 * The régime of the criterion: a disabled control leaves the tab order and its
 * text stops being selectable, and the screen made for READING a version - or a
 * vitrine - would be the one place where a list of words cannot be copied.
 */

export interface MethodFieldProps {
  readonly fieldRef: MethodFieldRef;
  readonly value: string;
  /** The text differs from the development version's - never while a version is only being read. */
  readonly touched: boolean;
  /** A version opened by « Voir », or a vitrine: nothing is typable. */
  readonly readOnly: boolean;
  readonly onChange: (text: string) => void;
}

/** The rows of each area: with the padding of the planche they give its heights (100, 214, 100, 81). */
const AREA_ROWS: Readonly<Record<string, number>> = {
  designates: 4,
  words: 10,
  recognizes: 4,
  typicalWords: 3,
};

const CONTROL =
  'w-full rounded-md border-2 text-gray-900 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 read-only:bg-gray-50';

export function MethodField({ fieldRef, value, touched, readOnly, onChange }: MethodFieldProps) {
  const id = useId();
  const mark = methodFieldId(fieldRef);
  const max = maxOf(fieldRef);
  const problem = readOnly ? null : problemOf(value, isRequired(fieldRef), max);
  const label =
    fieldRef.part === 'functions'
      ? METHOD_FUNCTION_FIELD_LABEL[fieldRef.field]
      : METHOD_STYLE_FIELD_LABEL[fieldRef.field];
  const optional = fieldRef.part === 'styles' && fieldRef.field === 'typicalWords';
  /** The planche draws a counter under the long words of a function, and under nothing else. */
  const counted = fieldRef.part === 'functions' && fieldRef.field === 'words';
  const line = fieldRef.part === 'styles' && fieldRef.field === 'name';

  const describedBy = [
    touched ? `${id}-etat` : null,
    counted ? `${id}-compte` : null,
    problem === null ? null : `${id}-refus`,
  ]
    .filter((one): one is string => one !== null)
    .join(' ');

  /** What the two kinds of control share: everything but their shape. */
  const shared = {
    id,
    value,
    readOnly,
    maxLength: max,
    'data-testid': mark,
    'data-touched': touched ? 'true' : 'false',
    ...(problem === null ? {} : { 'aria-invalid': true }),
    ...(describedBy === '' ? {} : { 'aria-describedby': describedBy }),
  } as const;
  const state =
    problem !== null
      ? 'border-rouge-deep bg-white'
      : touched
        ? 'border-jaune-deep bg-jaune-tint'
        : 'border-gray-300 bg-white';

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-h-4.75 items-center justify-between gap-2">
        <label
          htmlFor={id}
          className="font-semibold text-gray-600 text-micro uppercase tracking-wide"
        >
          {label}
          {optional ? (
            <>
              {' '}
              <span className="font-normal normal-case tracking-normal">
                {METHOD_OPTIONAL_MARK}
              </span>
            </>
          ) : null}
        </label>
        {touched ? (
          <span
            id={`${id}-etat`}
            data-testid={`${mark}-modifie`}
            className="inline-flex shrink-0 items-center rounded-pill border border-jaune bg-jaune-tint px-2 font-semibold text-jaune-deep text-micro leading-4.25"
          >
            {METHOD_TOUCHED_MARK}
          </span>
        ) : null}
      </div>

      {line ? (
        <input
          {...shared}
          type="text"
          autoComplete="off"
          onChange={(event) => {
            if (!readOnly) onChange(event.currentTarget.value);
          }}
          className={cn(CONTROL, state, 'h-9 px-2.5 text-sm')}
        />
      ) : (
        <textarea
          {...shared}
          rows={AREA_ROWS[fieldRef.field] ?? 4}
          {...(optional ? { placeholder: METHOD_TYPICAL_WORDS_PLACEHOLDER } : {})}
          onChange={(event) => {
            if (!readOnly) onChange(event.currentTarget.value);
          }}
          className={cn(CONTROL, state, 'block resize-y p-2.5 text-[13px] leading-[19px]')}
        />
      )}

      {counted ? (
        <p
          id={`${id}-compte`}
          data-testid={`${mark}-compteur`}
          className={cn(
            'self-end text-xs tabular-nums leading-4',
            problem === 'too-long' ? 'font-semibold text-rouge-deep' : 'text-gray-600',
          )}
        >
          {characterCount(value, max)}
        </p>
      ) : null}

      {problem === null ? null : (
        <p
          id={`${id}-refus`}
          role="alert"
          data-testid={`${mark}-refus`}
          className="text-rouge-deep text-sm"
        >
          {fieldRefusal(problem, value, max)}
        </p>
      )}
    </div>
  );
}
